import MuiAlert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { AlertProps, AlertSeverity } from './interface';
import React, { useState } from 'react';
import { faCheckCircle as faCheckCircleSolid } from '@medely/pro-solid-svg-icons/faCheckCircle';
import { faExclamationTriangle as faExclamationTriangleSolid } from '@medely/pro-solid-svg-icons/faExclamationTriangle';
import { faCircleInfo as faCircleInfoSolid } from '@medely/pro-solid-svg-icons/faCircleInfo';
import { faCircleExclamation as faCircleExclamationSolid } from '@medely/pro-solid-svg-icons/faCircleExclamation';
import { faClose as faCloseRegular } from '@medely/pro-regular-svg-icons/faClose';
import { FontAwesomeIcon } from '@medely/react-fontawesome';
import { Label, Text } from '../Typography';
import { DesignTokenColor, palette } from '@src/theme';
import { Button } from '../Button';
import { getTestId } from '@src/utils/testIds';

const TYPOGRAPHY_COLOR_MAPPING: Record<AlertSeverity, DesignTokenColor> = {
  error: 'state.error.primary',
  warning: 'state.warning.primary',
  info: 'state.info.primary',
  success: 'state.success.primary',
};

const SX_MAPPING = {
  outlined: {
    error: {},
    warning: {},
    info: {},
    success: {},
  },
  filled: {
    error: {
      backgroundColor: palette.state.error['backing-1'],
    },
    warning: {
      backgroundColor: palette.state.warning['backing-1'],
    },
    info: {
      backgroundColor: palette.state.info['backing-1'],
    },
    success: {
      backgroundColor: palette.state.success['backing-1'],
    },
  },
};

export const Alert = ({
  severity = 'info',
  variant = 'filled',
  title,
  description,
  hideCloseIcon,
  action,
  onClose,
  showIcon = true,
  ...otherProps
}: AlertProps) => {
  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };
  const typographyColor = TYPOGRAPHY_COLOR_MAPPING[severity];

  return (
    <Collapse in={open}>
      <MuiAlert
        data-testid={getTestId(otherProps)}
        iconMapping={{
          error: <FontAwesomeIcon icon={faCircleExclamationSolid} />,
          warning: <FontAwesomeIcon icon={faExclamationTriangleSolid} />,
          info: <FontAwesomeIcon icon={faCircleInfoSolid} />,
          success: <FontAwesomeIcon icon={faCheckCircleSolid} />,
        }}
        icon={showIcon ? undefined : false}
        severity={severity}
        variant={variant === 'filled' ? 'standard' : 'outlined'}
        action={
          <>
            {action && (
              <Button onClick={action.onClick} variant="outlined">
                {' '}
                {action.text}
              </Button>
            )}
            {!hideCloseIcon && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleClose}
                sx={{ marginLeft: 1 }}
              >
                <FontAwesomeIcon icon={faCloseRegular} />
              </IconButton>
            )}
          </>
        }
        sx={{
          ...SX_MAPPING[variant][severity],
        }}
      >
        {title && (
          <Label size="m" color={typographyColor}>
            {title}
          </Label>
        )}
        {description && (
          <Text size="m" color={typographyColor}>
            {description}
          </Text>
        )}
      </MuiAlert>
    </Collapse>
  );
};
